import { useCallback, useMemo, useRef } from 'react'
import styled, { css } from 'styled-components'
import { LINK_MOVE_TYPE } from 'core/constants'
import useProductInfo from 'core/hooks/useProductInfo'
import { moveToLink } from 'core/util/url'
import { useRecoilValue } from 'recoil'
import { STORE_AIRBRIDGE } from 'core/store/analysis'
import ButtonAlarm from 'components/Button/ButtonAlarm'
import useHandleAlarm from 'core/hooks/useHandleAlarm'
import { IS_OPEN_LINK, STORE_ROOT_PROPS } from 'core/store/global'
import { timeParser, layoutSmall, requestBridge } from 'core/util/default'
import { t } from 'i18next'
import { BRIDGE, ONLY_POST_MSG_PARTNERS } from 'core/constants/bridge'
import { getConfig, getMemberId, getPartnerId, getUA } from 'core/singleton'
import { CONFIRM_ALARM } from 'core/store/alarm'
import { BroadcastParams } from '@types'

interface StyledWrapperProps {
  dir?: 'column'
  gap?: number
  width?: string
  marginLeft?: string
}

interface BroadcastCardProps {
  item: BroadcastItem
}

const BroadcastCard: React.FC<BroadcastCardProps> = ({ item }) => {
  const {
    productListDetail,
    broadcastName,
    broadcastThumbnailList,
    broadcastStateCode,
    broadcastId,
    outputPath,
    programmingStartDt,
  } = item
  const root = useRecoilValue(STORE_ROOT_PROPS)

  const airbridge = useRecoilValue(STORE_AIRBRIDGE)
  const isOpenLink = useRecoilValue(IS_OPEN_LINK)
  const confirmAlarm = useRecoilValue(CONFIRM_ALARM)
  const { isSDK } = getUA()

  const isConfirmAlarm = confirmAlarm.some(
    ({ broadcastId: confirmBroadcastId }) => broadcastId === confirmBroadcastId,
  )

  const {
    productThumbnailUrl,
    productName,
    brandName,
    discountRate,
    sellingPrice,
    price,
    priceTypeCode,
    discountTypeCode,
    handleClickProduct,
  } = useProductInfo({ broadcastUrl: outputPath, productListDetail })
  const productRef = useRef<HTMLDivElement>(null)

  const isBeforeBroadcast = useMemo(() => {
    return (
      broadcastStateCode === 'accepted' ||
      broadcastStateCode === 'ready' ||
      broadcastStateCode === 'standby'
    )
  }, [broadcastStateCode])

  const isBroadcastEnd = useMemo(() => {
    return (
      broadcastStateCode === 'cancel' || broadcastStateCode === null || broadcastStateCode === 'vod'
    )
  }, [broadcastStateCode])

  const startTime = timeParser(programmingStartDt)

  const broadcastThumbnailUrl = broadcastThumbnailList[0]?.thumbnailUrl
  const isBeforeBroadcastStartTime = new Date(programmingStartDt) > new Date()
  const handleClickCard = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      e.preventDefault()

      if (productRef.current?.contains(e.target as HTMLElement)) {
        return
      }
      const params: BroadcastParams = {
        broadcastUrl: item.outputPath,
      }
      const partnerId = getPartnerId()
      if (ONLY_POST_MSG_PARTNERS.includes(partnerId)) {
        params.broadcastId = broadcastId
      }
      if (root?.bridge || window[BRIDGE.BROADCAST] || isSDK) {
        requestBridge(BRIDGE.BROADCAST, params)
        return
      }
      moveToLink({
        airbridge,
        link: item.outputPath,
        type: isOpenLink ? LINK_MOVE_TYPE.REPLACE : LINK_MOVE_TYPE.REDIRECT,
        confirmedAlarmMemberId: isConfirmAlarm ? getMemberId() : '',
      })
    },
    [item.outputPath, root?.bridge, isSDK, isConfirmAlarm, airbridge, isOpenLink, broadcastId],
  )
  const { handleClickAlarm } = useHandleAlarm()
  const useAlarm = getConfig().alarm?.use

  return (
    <StyledCard onClick={handleClickCard}>
      <StyledThumbnailWrapper
        isDimmed={isBeforeBroadcast || isBroadcastEnd}
        tabIndex={0}
        url={broadcastThumbnailUrl}
      >
        {isBeforeBroadcast && (
          <>
            {isBeforeBroadcastStartTime ? (
              <StyledBroadcastStatus>{startTime}</StyledBroadcastStatus>
            ) : (
              <StyledBroadcastStatus>{t('broadcast_ready')}</StyledBroadcastStatus>
            )}
          </>
        )}
        {isBroadcastEnd && <StyledBroadcastStatus>{t('broadcast_end')}</StyledBroadcastStatus>}
      </StyledThumbnailWrapper>
      <StyledInfoContainer>
        <StyledInfoContainerWrapper>
          <StyledInfoTitle>{broadcastName}</StyledInfoTitle>
          {useAlarm && isBeforeBroadcastStartTime && (
            <ButtonAlarm
              isAlarm={isConfirmAlarm}
              className="alarm-button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                e.preventDefault()
                handleClickAlarm({
                  broadcastId: item.broadcastId,
                  ScheduleItem: item,
                })
              }}
            />
          )}
        </StyledInfoContainerWrapper>
        <StyledWrapper dir="column" gap={10}>
          {!!brandName && <StyledBrandName>{brandName}</StyledBrandName>}
          {/** TODO 구매인증 배지 처리 관련 확인 필요
           * <StyledBadge>
            <IconPurchaseAuth />
           {t('purchase_auth')}
        </StyledBadge>*/}
          <StyledProductWrapper
            id="product"
            tabIndex={productName ? 0 : -1}
            ref={productRef}
            onClick={handleClickProduct}
          >
            {productThumbnailUrl && (
              <StyledProductThumbnail src={productThumbnailUrl} alt={productName} />
            )}
            <StyledPriceContainer>
              <StyledBrandWrapper>
                {!!productName && <StyledProductName>{productName}</StyledProductName>}
              </StyledBrandWrapper>
              <StyledPriceWrapper>
                {priceTypeCode === 'basic' &&
                  (discountTypeCode === 'none' ? (
                    <StyledPrice>{price}</StyledPrice>
                  ) : (
                    <>
                      <StyledDiscountRate>{discountRate}%</StyledDiscountRate>
                      <StyledPrice>{sellingPrice}</StyledPrice>
                    </>
                  ))}
                {priceTypeCode === 'text' && <StyledPriceText>{price}</StyledPriceText>}
              </StyledPriceWrapper>
            </StyledPriceContainer>
          </StyledProductWrapper>
        </StyledWrapper>
      </StyledInfoContainer>
    </StyledCard>
  )
}

const StyledWrapper = styled.div<StyledWrapperProps>`
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  ${({ width }) => width && `width: ${width};`}
  ${({ marginLeft }) => marginLeft && `margin-left: ${marginLeft};`}
  ${({ dir }) => dir && `flex-direction: column;`}
  ${({ gap }) => gap && `gap: ${gap}px;`}
`

const StyledCard = styled.div`
  display: flex;
  width: 100%;
  height: 240px;
  cursor: pointer;
  margin-bottom: 40px;

  &:last-of-type {
    margin-bottom: 50px;
  }

  @media screen and (max-width: 768px) {
    height: 134px;
    margin-bottom: 20px;

    &:last-of-type {
      margin-bottom: 30px;
    }
  }
`

const StyledThumbnailWrapper = styled.div<{ isDimmed: boolean; url: string }>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 180px;
  height: 100%;
  border-radius: 6px;
  flex-shrink: 0;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  ${({ url }) => `background-image: url(${url});`}
  ${({ isDimmed }) =>
    isDimmed &&
    `&::after {
    content: '';
    border-radius: inherit;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.30);
  }`}
  @media screen and (max-width: 768px) {
    border-radius: 4px;
    width: 100px;
  }
`

const StyledBroadcastStatus = styled.span`
  position: absolute;
  color: #f8f8f9;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.2);
  font-size: 22px;
  font-weight: 700;
  line-height: 18px; /* 81.818% */
  z-index: 1;
  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`

const StyledInfoContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-left: 40px;
  overflow: hidden;
  width: 100%;
  @media screen and (max-width: 768px) {
    margin-left: 12px;
  }
`

const StyledInfoTitle = styled.span`
  flex: 1;
  color: #1b1c1d;
  display: -webkit-box;
  word-wrap: break-word;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  text-overflow: ellipsis;
  overflow: hidden;
  font-size: 20px;
  font-weight: 500;
  line-height: 20px; /* 100% */
  letter-spacing: -0.2px;
  width: fit-content;
  height: fit-content;
  flex-shrink: 0;
  @media screen and (max-width: 768px) {
    font-size: 14px;
  }
`

const StyledProductWrapper = styled.div`
  display: flex;
  gap: 10px;
  z-index: 1;
`

const StyledProductThumbnail = styled.img`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 4px;
`

const StyledPriceContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 2px;
  overflow: hidden;
`

const StyledPriceWrapper = styled.div`
  display: flex;
  gap: 4px;
`

const StyledDiscountRate = styled.span`
  color: #ff383b;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px; /* 128.571% */
`

const StyledPrice = styled.span`
  color: #1b1c1d;
  font-size: 14px;
  font-weight: 700;
  line-height: 18px; /* 128.571% */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledPriceText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #ffffff;
  font-weight: bold;
  font-size: 14px;
  line-height: 18px;
`

const StyledBrandWrapper = styled.div`
  display: flex;
  gap: 6px;
`

const StyledBrandName = styled.span`
  color: #555;
  font-size: 13px;
  font-weight: 700;
  line-height: 125%; /* 16.25px */
  margin-right: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledProductName = styled.span`
  color: #555;
  font-size: 13px;
  line-height: 125%; /* 16.25px */
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const StyledInfoContainerWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  .alarm-button {
    min-width: 129px;
    height: 40px;
    margin-left: 8px;
    border-radius: 100px;
    svg {
      width: 24px;
      height: 24px;
    }
    span {
      font-size: 14px;
      font-weight: 700;
      line-height: 14px;
    }
  }

  ${layoutSmall(css`
    .alarm-button {
      min-width: auto;
      width: 32px;
      height: 32px;
      padding: 0;
      border-radius: 16px;
      svg {
        width: 16px;
        height: 16px;
      }
      span {
        display: none;
      }
    }
  `)}
`

// const StyledBadge = styled.div`
//   display: flex;
//   justify-content: center;
//   align-items: center;
//   width: fit-content;
//   padding: 6px;
//   gap: 4px;
//   border-radius: 4px;
//   border: 1px solid #f1f1f1;
//   background: #fff;
//   color: #616568;
//   font-size: 13px;
//   font-weight: 400;
//   line-height: 20px; /* 153.846% */
// `

export default BroadcastCard
